// Central place for environment variables, API endpoints, and site-wide content.
// Import from here instead of reading process.env or hardcoding strings elsewhere.

import vendingTwoPeople from './assets/vendingTwoPeople.png';
import vendingFinger from './assets/vendingFinger.png';
import logoMark from './assets/logo-mark.png';
import logoFull from './assets/logo-full.png';

export const ENV = {
  CONTACT_FORM_ENDPOINT: process.env.REACT_APP_CONTACT_FORM_ENDPOINT || '',
};

export const IMAGES = {
  vendingTwoPeople: { src: vendingTwoPeople, alt: 'Two people browsing an Orbit vending machine' },
  vendingFinger: { src: vendingFinger, alt: 'Close-up of a hand tapping an Orbit machine touchscreen' },
};

export const LOGO = {
  mark: { src: logoMark, alt: 'Orbit' },
  full: { src: logoFull, alt: 'Orbit — Convenience, thoughtfully designed.' },
};

export const SITE = {
  name: 'Orbit',
  tagline: 'Convenient by design.',
  missionStatement: 'Orbit exists to make everyday convenience feel intentional.',
  mission:
    'We provide thoughtfully curated food, beverages, and everyday essentials through modern self-service markets and vending solutions that seamlessly fit into the places people work, live, learn, and travel. By combining quality products, intuitive technology, and reliable service, Orbit creates spaces that people trust—not simply machines they use.',
  vision:
    'To become the most trusted provider of modern self-service retail by redefining convenience through thoughtful design, dependable service, and carefully selected products.',
  promise:
    'Orbit delivers modern convenience through thoughtfully designed self-service experiences that people trust every day.',
  
    footerCredit: '© 2026 Orbit. All rights reserved. Website created by Christina Zimmer',
};

export const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Packages', path: '/packages' },
  { label: 'About', path: '/about' },
];

export const BRAND_VOICE = [
  'Ready when you are.',
  'Thoughtfully stocked.',
  'Convenient by design.',
  'Everyday essentials, exactly where you need them.',
];

export const BRAND_PRINCIPLES = [
  {
    id: 'convenience',
    title: 'Convenience without compromise',
    description: 'Access to quality products should be simple, fast, and available where people already are.',
  },
  {
    id: 'intention',
    title: 'Designed with intention',
    description: 'Every machine, market, and interaction should feel clean, organized, and easy to use.',
  },
  {
    id: 'reliability',
    title: 'Reliability first',
    description: 'Consistent inventory, dependable technology, and responsive service are the foundation of the Orbit experience.',
  },
  {
    id: 'quality',
    title: 'Quality matters',
    description: 'We prioritize products people genuinely enjoy—from everyday favorites to healthier alternatives and locally sourced options when possible.',
  },
  {
    id: 'growth',
    title: 'Built to grow',
    description: 'Orbit is designed as a scalable convenience platform capable of expanding into new environments and services.',
  },
];

// "description" is the short teaser used on the Home page list — keep it
// stable. Everything else here is the detail shown on the Packages page.
export const PACKAGES = [
  {
    id: 'essentials',
    name: 'Orbit Essentials',
    description: 'Traditional vending designed for reliable everyday convenience.',
    subtitle: 'Everyday Convenience',
    focus: 'Reliable everyday favorites.',
    designedFor: ['Manufacturing facilities', 'Warehouses', 'Schools', 'Offices', 'Public spaces'],
    categories: [
      {
        group: null,
        items: [
          'Soft drinks', 'Water', 'Juice', 'Sports drinks', 'Energy drinks', 'Chips',
          'Candy', 'Cookies', 'Crackers', 'Gum', 'Trail mix', 'Everyday snack bars',
        ],
      },
    ],
    notes: { label: '', 
      items: [


      ],
      
    },
  },
  {
    id: 'refresh',
    name: 'Orbit Refresh',
    description: 'Premium beverage solutions.',
    subtitle: 'Beverage Experience',
    focus: 'Focused on hydration and energy throughout the workday.',
    designedFor: ['Offices', 'Coworking spaces', 'Corporate campuses', 'Break rooms'],
    categories: [
      {
        group: null,
        items: [
          'Fresh coffee', 'Cold brew', 'Sparkling water', 'Flavored water', 'Energy drinks',
          'Functional beverages', 'Tea', 'Juice', 'Sports drinks', 'Light snack pairings',
        ],
      },
    ],
  },
  {
    id: 'select',
    name: 'Orbit Select',
    description: 'Curated premium snacks and wellness products.',
    subtitle: 'Curated Better-For-You Collection',
    focus: 'Designed for organizations prioritizing employee wellness and premium experiences.',
    designedFor: ['Corporate offices', 'Wellness-focused workplaces', 'Premium environments'],
    categories: [
      {
        group: null,
        items: [
          'Protein bars', 'Protein snacks', 'Trail mix', 'Nuts', 'Dried fruit', 'Kombucha',
          'Sparkling beverages', 'Vegan snacks', 'Gluten-free products', 'Organic chips',
          'Local artisan products', 'Functional beverages',
        ],
      },
    ],
  },
  {
    id: 'living',
    name: 'Orbit Living',
    description: 'Convenience solutions for residential communities.',
    subtitle: 'Everyday Living Essentials',
    focus: 'Extends beyond food to provide convenient access to daily necessities.',
    designedFor: ['Luxury apartments', 'Condominiums', 'Student housing', 'Hotels', 'Residential communities'],
    categories: [
      { group: 'Food & Beverage', items: ['Snacks', 'Frozen meals', 'Grab-and-go breakfast', 'Beverages', 'Coffee', 'Water'] },
      { group: 'Household Essentials', items: ['Paper towels', 'Toilet paper', 'Trash bags', 'Laundry detergent', 'Dryer sheets', 'Dish soap', 'Sponges'] },
      {
        group: 'Personal Care',
        items: [
          'Toothpaste', 'Toothbrushes', 'Deodorant', 'Shampoo', 'Conditioner', 'Razors',
          'Feminine hygiene products', 'Pain relievers', 'Bandages', 'Hand sanitizer',
        ],
      },
      { group: 'Convenience Items', items: ['Phone chargers', 'Batteries', 'Umbrellas', 'Over-the-counter medications', 'Pet treats (where appropriate)'] },
    ],
   
  },
];

// Orbit's merchandising philosophy — shown as framing copy on the Packages page.
export const MERCHANDISING = {
  philosophy: 'The goal is to eliminate small trips to the convenience store by making everyday essentials available where residents already live.',
  intro:
    'Orbit is not built around the largest possible selection. Every collection is a balanced, curated assortment—healthy options alongside indulgent treats, familiar favorites alongside something new to discover—so every shelf feels considered, never cluttered.',
  finalExperience:
    'Orbit should never feel like a vending machine filled with random products. It should feel like a beautifully organized neighborhood market—scaled down into a self-service experience.',
};

export const FUTURE_EXPANSION = [
  'Orbit Campus',
  'Orbit Transit',
  'Orbit Health',
  'Orbit Hospitality',
  'Orbit Express',
  'Orbit Mobile',
  'Orbit Go',
];
